import { useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines } from "@/components/motion";
import { Menu, X } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const CodeBlock = ({ title, children }) => (
  <div className="border border-border my-6">
    {title && (
      <div className="border-b border-border bg-muted/60 px-4 py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
        {title}
      </div>
    )}
    <pre className="bg-[#0A0A0A] text-[#C9D1D9] font-mono text-xs leading-relaxed p-5 overflow-x-auto">
      <code>{children}</code>
    </pre>
  </div>
);

const Inline = ({ children }) => (
  <code className="font-mono text-[0.85em] bg-muted px-1.5 py-0.5 text-telemetry">{children}</code>
);

const MethodBadge = ({ method }) => (
  <span
    className={`font-mono text-[10px] tracking-widest px-2.5 py-1 ${
      method === "POST" ? "bg-foreground text-background" : "border border-foreground"
    }`}
  >
    {method}
  </span>
);

const Endpoint = ({ id, method, path, desc, request, response }) => (
  <div id={id} data-testid={`docs-endpoint-${id}`} className="border-t border-border pt-10 mt-10 scroll-mt-24">
    <div className="flex items-center gap-4 flex-wrap">
      <MethodBadge method={method} />
      <code className="font-mono text-lg font-semibold text-telemetry">{path}</code>
    </div>
    <p className="text-sm text-muted-foreground leading-relaxed mt-4 max-w-2xl">{desc}</p>
    {request && <CodeBlock title="Request">{request}</CodeBlock>}
    {response && <CodeBlock title="Response · 200">{response}</CodeBlock>}
  </div>
);

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`docs-${id}`} className="mb-20 scroll-mt-24">
    <h2 className="font-display font-extrabold tracking-tight text-3xl lg:text-4xl border-b border-border pb-5 mb-8">
      {title}
    </h2>
    <div className="text-sm leading-relaxed text-foreground/90 max-w-3xl space-y-4">{children}</div>
  </section>
);

const NAV = [
  ["quickstart", "Quickstart"],
  ["truth-definition", "Truth Definition"],
  ["overview", "Overview"],
  ["authentication", "Authentication"],
  ["data-model", "Data Model"],
  ["truth-engine", "Truth Engine"],
  ["compliance", "Compliance Rules"],
  ["anomalies", "Anomaly Detection"],
  ["audit", "Audit Layer"],
  ["endpoints", "Endpoint Reference"],
  ["webhooks", "Webhooks"],
  ["errors", "Error Codes"],
  ["best-practices", "Best Practices"],
];

const EVENT_JSON = `{
  "event_id": "evt_9f31ac02b7",
  "route_id": "rt_monroe_4412",
  "driver_id": "drv_8834",
  "device_id": "dev_px7a_221",
  "planned_coordinates":  { "lat": 32.5200,  "lng": -92.1100 },
  "actual_coordinates":   { "lat": 32.52005, "lng": -92.11003 },
  "planned_eta": "2026-07-14T14:30:00Z",
  "timestamp":   "2026-07-14T14:31:12Z",
  "claimed_event_type":  "delivered",
  "observed_event_type": "delivered",
  "telemetry": {
    "speed_kmh": 0, "heading": 212, "accuracy_m": 7, "battery": 68,
    "previous_coordinates": { "lat": 32.5089, "lng": -92.1041 },
    "previous_timestamp": "2026-07-14T14:24:00Z",
    "photo": { "gps_embedded": true, "timestamp": "2026-07-14T14:31:10Z",
               "hash": "9f2c1ab7…" }
  }
}`;

const VERIFY_RESPONSE = `{
  "event_id": "evt_9f31ac02b7",
  "truth_status": "true",
  "distance_delta_m": 6.1,
  "time_delta_minutes": 1.2,
  "telemetry_score": 1.0,
  "claim_consistency_score": 1.0,
  "scenario_tags": ["clean_delivery"],
  "compliance_flags": [
    "arrival_window_met", "deviation_radius_ok",
    "proximity_validated", "evidence_complete"
  ],
  "compliance_score": 1.0,
  "regulatory_notes": [],
  "anomaly_tags": [],
  "severity_score": 0.0,
  "recommended_actions": [],
  "audit_signature": "3f9a…c21d",
  "audit_seq": 4182,
  "evaluated_at": "2026-07-14T14:31:13.104Z"
}`;

const Docs = () => {
  const [open, setOpen] = useState(false);

  const navList = (
    <nav className="space-y-3">
      {NAV.map(([id, label]) => (
        <a
          key={id}
          data-testid={`docs-nav-${id}`}
          href={`#${id}`}
          onClick={() => setOpen(false)}
          className="block font-mono text-[11px] tracking-[0.15em] uppercase text-muted-foreground hover:text-telemetry transition-colors duration-200"
        >
          {label}
        </a>
      ))}
    </nav>
  );

  return (
    <div data-testid="docs-page" className="bg-background text-foreground min-h-screen">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-28 pb-24">
        <header className="mb-16 border-b border-border pb-12">
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-telemetry mb-6">
            GeoVerify Developer Documentation · v0.1
          </p>
          <h1 className="font-display font-black tracking-tight text-5xl lg:text-7xl leading-[0.92]">
            <MaskedLines lines={["Verify everything.", "Trust the trail."]} delay={0.15} />
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Production: <Inline>{`${BASE}/api/v1`}</Inline> (requires{" "}
            <Inline>X-GeoVerify-Key</Inline>) · Sandbox: <Inline>{`${BASE}/api`}</Inline> (no key,
            mock telemetry) — routing-agnostic verification for any delivery, dispatch, TMS, or
            fleet stack.
          </p>
        </header>

        <button
          data-testid="docs-mobile-nav-btn"
          onClick={() => setOpen(!open)}
          className="lg:hidden mb-8 border border-border font-mono text-[11px] tracking-widest uppercase px-4 py-2.5 flex items-center gap-2"
        >
          {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />} Contents
        </button>
        {open && <div className="lg:hidden border border-border p-6 mb-10 bg-card">{navList}</div>}

        <div className="grid lg:grid-cols-12 gap-12">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28 border border-border bg-card p-6">{navList}</div>
          </aside>

          <div className="lg:col-span-9">
            <Section id="quickstart" title="Quickstart">
              <p>Three steps to your first verified event:</p>
              <div className="border border-border divide-y divide-border font-mono text-xs mt-6">
                {[
                  ["1", "Set the X-GeoVerify-Key header on every request (demo tenant key: gvk_demo_7f3a9c1e55d24b8e6a01 — request your own via Request Access)."],
                  ["2", "POST /event/ingest with a raw delivery event — coordinates, timestamps, claims, telemetry."],
                  ["3", "POST /event/verify and read truth_status in the response: true, partial, or false."],
                ].map(([n, step]) => (
                  <div key={n} className="flex gap-4 px-5 py-4">
                    <span className="text-telemetry font-semibold">{n}</span>
                    <span className="leading-relaxed">{step}</span>
                  </div>
                ))}
              </div>
              <CodeBlock title="60-second integration">
{`curl -X POST ${BASE}/api/v1/event/verify \\
  -H "Content-Type: application/json" \\
  -H "X-GeoVerify-Key: gvk_demo_7f3a9c1e55d24b8e6a01" \\
  -d '{ "event": { "planned_coordinates": { "lat": 32.52, "lng": -92.11 },
        "actual_coordinates": { "lat": 32.52005, "lng": -92.11003 },
        "planned_eta": "2026-07-14T14:30:00Z",
        "timestamp": "2026-07-14T14:31:12Z",
        "claimed_event_type": "delivered", "observed_event_type": "delivered",
        "telemetry": { "speed_kmh": 0, "accuracy_m": 7 } } }'

# → { "truth_status": "true", "distance_delta_m": 6.1, … }`}
              </CodeBlock>
              <p>
                Versioning: production traffic targets <Inline>/api/v1</Inline>. The unversioned{" "}
                <Inline>/api</Inline> sandbox remains keyless for evaluation and powers the public
                playground.
              </p>
            </Section>

            <Section id="truth-definition" title="Truth Definition">
              <p>
                Everything GeoVerify returns derives from one formula. An event is TRUE only when
                all of the following hold:
              </p>
              <CodeBlock title="The truth condition">
{`TRUTH = distance(actual, planned)  <  max_deviation_radius_meters
      AND timestamp within allowed_arrival_window
      AND telemetry consistent (score >= threshold)
      AND claim matches observed event
      AND evidence present and validated
      AND geofence rules satisfied`}
              </CodeBlock>
              <p>
                Anything less decomposes into <Inline>partial</Inline> or <Inline>false</Inline>,
                with the exact failing signals returned as deltas, flags, and scenario tags. No
                black boxes — every verdict is reconstructible from the response.
              </p>
            </Section>

            <Section id="overview" title="Overview">
              <p>
                GeoVerify is the truth layer for delivery networks. Your routing engine provides the
                plan, your driver provides the claim, your devices provide the telemetry — GeoVerify
                tells you what actually happened. Every event you ingest is evaluated by three
                deterministic engines (truth, compliance, anomaly) and sealed into an immutable,
                SHA-256 chained audit trail.
              </p>
              <p>
                The typical flow: <Inline>POST /event/ingest</Inline> →{" "}
                <Inline>POST /event/verify</Inline> → retrieve results via{" "}
                <Inline>GET /event/truth</Inline>, <Inline>GET /event/compliance</Inline>, and{" "}
                <Inline>GET /event/audit</Inline>.
              </p>
            </Section>

            <Section id="authentication" title="Authentication">
              <p>
                Production tenants authenticate with an API key sent in the{" "}
                <Inline>X-GeoVerify-Key</Inline> header. Keys are scoped per tenant; every record is
                tenant-isolated. OAuth2/JWT federation is available on the Enterprise tier.
              </p>
              <CodeBlock title="Authenticated request">
{`curl -X POST ${BASE}/api/v1/event/verify \\
  -H "Content-Type: application/json" \\
  -H "X-GeoVerify-Key: gvk_live_…" \\
  -d '{ "event": { … } }'`}
              </CodeBlock>
              <p>
                The sandbox endpoints (<Inline>/api/*</Inline>) powering the public playground
                require no key and run against mock telemetry only. Production endpoints live under{" "}
                <Inline>/api/v1</Inline> and reject requests without a valid key with{" "}
                <Inline>401</Inline>.
              </p>
            </Section>

            <Section id="data-model" title="Data Model">
              <p>
                The event object is the atomic unit of truth. Coordinates are WGS-84 decimal
                degrees; timestamps are ISO-8601 UTC.
              </p>
              <CodeBlock title="Event object">{EVENT_JSON}</CodeBlock>
              <div className="border border-border mt-6">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-border bg-muted/60 text-left text-muted-foreground uppercase tracking-widest text-[10px]">
                      <th className="px-4 py-2.5 font-medium">Field</th>
                      <th className="px-4 py-2.5 font-medium">Type</th>
                      <th className="px-4 py-2.5 font-medium">Required</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {[
                      ["planned_coordinates / actual_coordinates", "{lat, lng}", "yes"],
                      ["timestamp / planned_eta", "ISO-8601 UTC", "yes"],
                      ["claimed_event_type / observed_event_type", "string", "yes"],
                      ["telemetry.speed_kmh / accuracy_m / heading", "number", "recommended"],
                      ["telemetry.photo.{gps_embedded, timestamp, hash}", "object", "per ruleset"],
                      ["route_id / driver_id / device_id", "string", "recommended"],
                    ].map(([f, t, r]) => (
                      <tr key={f}>
                        <td className="px-4 py-2.5">{f}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{t}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{r}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section id="truth-engine" title="Truth Engine Logic">
              <p>An event is TRUE when every one of these holds:</p>
              <CodeBlock title="Truth condition">
{`truth = distance(actual, planned)   <  max_deviation_radius_meters   (150 m)
      AND |timestamp − planned_eta|   <= allowed_arrival_window_minutes (10 min)
      AND telemetry_score             >= telemetry_consistency_threshold (0.80)
      AND claimed_event_type          == observed_event_type
      AND photo metadata validated (GPS + timestamp + hash)
      AND geofence rules satisfied`}
              </CodeBlock>
              <p>
                Output includes <Inline>truth_status</Inline> (true / partial / false),{" "}
                <Inline>distance_delta_m</Inline>, <Inline>time_delta_minutes</Inline>,{" "}
                <Inline>telemetry_score</Inline>, <Inline>claim_consistency_score</Inline>, and{" "}
                <Inline>scenario_tags</Inline> such as <Inline>late_arrival</Inline>,{" "}
                <Inline>off_route</Inline>, <Inline>geofence_breach</Inline>,{" "}
                <Inline>telemetry_mismatch</Inline>.
              </p>
            </Section>

            <Section id="compliance" title="Compliance Rules">
              <p>
                The compliance engine applies regulatory-grade checks: geofence entry/exit
                (point-in-polygon over your registered polygons), arrival window compliance,
                proximity validation, route adherence, device-vs-driver claim consistency, and
                required evidence presence (photo, signature, telemetry).
              </p>
              <p>
                Output: <Inline>compliance_flags</Inline> (e.g. <Inline>arrival_window_met</Inline>,{" "}
                <Inline>geofence_breach</Inline>), a normalized <Inline>compliance_score</Inline>,
                and human-readable <Inline>regulatory_notes</Inline> suitable for audit packets.
              </p>
            </Section>

            <Section id="anomalies" title="Anomaly Detection">
              <div className="border border-border">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/60 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      <th className="px-4 py-2.5 font-medium">Anomaly</th>
                      <th className="px-4 py-2.5 font-medium">Trigger</th>
                      <th className="px-4 py-2.5 font-medium">Severity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border font-mono">
                    {[
                      ["impossible_travel_speed", "speed > 160 km/h at event time", "0.95"],
                      ["location_jump", "implied speed between fixes > 160 km/h", "0.90"],
                      ["claim_contradiction", "claimed ≠ observed event type", "0.80"],
                      ["gps_drift_suspected", "accuracy_m > 100 at event time", "0.60"],
                      ["missing_evidence", "required photo metadata absent", "0.40"],
                      ["bearing_mismatch", "heading diverges > 60° from movement vector", "0.55"],
                      ["speed_trace_mismatch", "reported vs implied speed diverge > 3×", "0.50"],
                      ["evidence_time_gap", "photo timestamp > 120s from event", "0.45"],
                      ["arrived_at_speed", "delivery claimed while moving > 40 km/h", "0.40"],
                      ["telemetry_fabricated", "2+ physics breaks with zero telemetry credibility", "scenario"],
                    ].map(([a, t, s]) => (
                      <tr key={a}>
                        <td className="px-4 py-2.5 text-anomaly">{a}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">{t}</td>
                        <td className="px-4 py-2.5">{s}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                Each anomaly ships with a severity score and a recommended action — quarantine the
                event, freeze the claim payout, or request re-verification.
              </p>
            </Section>

            <Section id="audit" title="Audit Layer">
              <p>
                Every verification writes an append-only audit record. Records are SHA-256 chained
                (each signature covers the previous one), timestamped, and exportable — ready to
                hand to insurers, regulators, and internal audit.
              </p>
              <CodeBlock title="GET /event/audit/{event_id} — response excerpt">
{`{
  "event_id": "evt_9f31ac02b7",
  "chain_length": 2,
  "records": [{
    "seq": 4182,
    "signature": "3f9a…c21d",
    "previous_signature": "a01b…77e4",
    "algorithm": "SHA-256 chain (GV1)",
    "reconciliation_log": [
      "planned vs actual distance delta: 6.1 m",
      "arrival time delta: 1.2 min",
      "telemetry consistency score: 1.0",
      "compliance score: 1.0 (4 checks)"
    ],
    "immutable": true,
    "created_at": "2026-07-14T14:31:13.104Z"
  }]
}`}
              </CodeBlock>
              <p>
                <Inline>GET /event/audit/{"{event_id}"}/export</Inline> downloads a signed,
                self-contained audit packet (JSON attachment) combining the truth evaluation,
                compliance record, and full chain — with an <Inline>export_signature</Inline> and a{" "}
                <Inline>chain_valid</Inline> integrity check. Built to hand directly to insurers and
                regulators.
              </p>
            </Section>

            <Section id="endpoints" title="Endpoint Reference">
              <Endpoint
                id="ep-ingest"
                method="POST"
                path="/event/ingest"
                desc="Send raw delivery events — coordinates, timestamps, route IDs, and driver claims. Returns an ingestion signature. Idempotent per event_id."
                request={`POST ${BASE}/api/v1/event/ingest\n\n${EVENT_JSON}`}
                response={`{\n  "event_id": "evt_9f31ac02b7",\n  "ingested_at": "2026-07-14T14:31:12.8Z",\n  "signature": "b7c1…9a0e"\n}`}
              />
              <Endpoint
                id="ep-verify"
                method="POST"
                path="/event/verify"
                desc="Run truth + compliance + anomaly engines over a single event. Optionally pass a rules object to override tenant defaults. Writes an audit record."
                request={`POST ${BASE}/api/v1/event/verify\n\n{\n  "event": { …event object… },\n  "rules": { "max_deviation_radius_meters": 150 }\n}`}
                response={VERIFY_RESPONSE}
              />
              <Endpoint
                id="ep-batch"
                method="POST"
                path="/event/verify/batch"
                desc="Verify up to 100 events in a single call. Each event runs the full truth, compliance, and anomaly pipeline and writes its own audit record. Returns per-event packets plus fleet-level tallies."
                request={`POST ${BASE}/api/v1/event/verify/batch\n\n{\n  "events": [ { …event… }, { …event… } ],\n  "rules": { "allowed_arrival_window_minutes": 15 }\n}`}
                response={`{\n  "total": 100,\n  "verified_true": 91,\n  "partial": 6,\n  "flagged_false": 3,\n  "results": [ { …verification packet per event… } ]\n}`}
              />
              <Endpoint
                id="ep-truth"
                method="GET"
                path="/event/truth/{event_id}"
                desc="Retrieve the stored truth evaluation for an event — distance delta, time delta, integrity scores, and scenario tags."
                response={`{\n  "event_id": "evt_9f31ac02b7",\n  "truth_status": "true",\n  "distance_delta_m": 6.1,\n  "time_delta_minutes": 1.2,\n  "telemetry_score": 1.0,\n  "claim_consistency_score": 1.0,\n  "scenario_tags": ["clean_delivery"]\n}`}
              />
              <Endpoint
                id="ep-compliance"
                method="GET"
                path="/event/compliance/{event_id}"
                desc="Retrieve compliance flags, the normalized compliance score, and regulatory notes for an event."
                response={`{\n  "event_id": "evt_9f31ac02b7",\n  "compliance_flags": ["arrival_window_met", "deviation_radius_ok"],\n  "compliance_score": 1.0,\n  "regulatory_notes": []\n}`}
              />
              <Endpoint
                id="ep-audit"
                method="GET"
                path="/event/audit/{event_id}"
                desc="Pull the immutable, chained audit trail for an event — signatures, reconciliation log, evidence hashes, truth and compliance summaries. Append /export for a signed downloadable packet."
              />
              <Endpoint
                id="ep-rules"
                method="GET"
                path="/rules  ·  PUT /rules"
                desc="Read or update your tenant ruleset — arrival window, deviation radius, proximity threshold, telemetry threshold, and named zones (service_area, depot, no_go) the compliance engine enforces. Editable visually in the Zone Editor (/geofences)."
                request={`PUT ${BASE}/api/v1/rules\n\n{\n  "max_deviation_radius_meters": 150,\n  "allowed_arrival_window_minutes": 10,\n  "geofence_polygon": [ { "lat": 32.51, "lng": -92.12 }, … ]\n}`}
                response={`{\n  "ok": true,\n  "tenant_id": "tn_4f2c9a01b3",\n  "rules": { …merged ruleset… }\n}`}
              />
              <Endpoint
                id="ep-webhooks"
                method="POST"
                path="/webhooks/register"
                desc="Register an HTTPS endpoint for signed deliveries — choose from event_verified, compliance_failed, anomaly_detected. The signing secret is returned exactly once. GET /webhooks lists subscriptions, DELETE /webhooks/{subscription_id} removes one."
                request={`POST ${BASE}/api/v1/webhooks/register\n\n{\n  "url": "https://your-system.example/geoverify-hook",\n  "events": ["anomaly_detected", "compliance_failed"]\n}`}
                response={`{\n  "subscription_id": "sub_7a19c2e04d",\n  "secret": "whsec_…  (shown once)",\n  "events": ["anomaly_detected", "compliance_failed"]\n}`}
              />
            </Section>

            <Section id="webhooks" title="Webhooks">
              <p>
                GeoVerify pushes verification outcomes to your systems the moment they happen. You
                register an HTTPS endpoint; we call it with a signed payload.
              </p>
              <div className="border border-border font-mono text-xs mt-6">
                {[
                  ["event_verified", "A verification completed — includes truth_status and deltas"],
                  ["compliance_failed", "Any compliance check failed — includes flags and regulatory notes"],
                  ["anomaly_detected", "An anomaly fired — includes tag, severity, recommended action"],
                ].map(([t, d], i) => (
                  <div key={t} className={`grid sm:grid-cols-12 gap-2 px-4 py-3 ${i ? "border-t border-border" : ""}`}>
                    <span className="sm:col-span-4 text-telemetry">{t}</span>
                    <span className="sm:col-span-8 text-muted-foreground">{d}</span>
                  </div>
                ))}
              </div>
              <CodeBlock title="Webhook payload — event_verified">
{`POST https://your-system.example/geoverify-hook
X-GeoVerify-Signature: hmac-sha256=…
X-GeoVerify-Event: event_verified

{
  "event_id": "evt_9f31ac02b7",
  "truth_status": "true",
  "compliance_score": 1.0,
  "anomaly_tags": [],
  "audit_signature": "3f9a…c21d",
  "occurred_at": "2026-07-14T14:31:13.104Z"
}`}
              </CodeBlock>
              <p>
                Verify every delivery with the <Inline>X-GeoVerify-Signature</Inline> HMAC header —
                an HMAC-SHA256 of the raw request body using your subscription secret. Rotate
                secrets anytime via <Inline>POST /webhooks/{"{subscription_id}"}/rotate-secret</Inline>{" "}
                — signing switches immediately and the previous secret remains acceptable for a 24h
                grace period, so rotation causes no downtime. Failed
                deliveries retry automatically with backoff (+10s, +30s, +90s) and every attempt is
                logged for audit. Registration is live:{" "}
                <Inline>POST /api/v1/webhooks/register</Inline> (see Endpoint Reference).
              </p>
            </Section>

            <Section id="errors" title="Error Codes">
              <div className="border border-border font-mono text-xs">
                {[
                  ["400", "Malformed coordinates or timestamp", "Validate WGS-84 ranges and ISO-8601 format"],
                  ["404", "No record for event_id", "Verify the event before retrieving truth/audit"],
                  ["422", "Missing required field", "See field table in Data Model"],
                  ["429", "Rate limit exceeded", "Per-key per-minute limit (default 60, admin-tunable); honor Retry-After and back off"],
                  ["5xx", "Engine unavailable", "Retry with idempotency key; events are never double-charged"],
                ].map(([code, msg, fix], i) => (
                  <div key={code} className={`grid sm:grid-cols-12 gap-2 px-4 py-3 ${i ? "border-t border-border" : ""}`}>
                    <span className="sm:col-span-2 text-anomaly font-semibold">{code}</span>
                    <span className="sm:col-span-5">{msg}</span>
                    <span className="sm:col-span-5 text-muted-foreground">{fix}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="best-practices" title="Best Practices">
              <ul className="list-none space-y-3 font-mono text-xs">
                {[
                  "INGEST AT EVENT TIME — verification quality degrades with delayed telemetry.",
                  "ALWAYS SEND previous_coordinates — it powers location-jump and spoofing detection.",
                  "USE STABLE event_id VALUES — ingest and verify are idempotent per ID.",
                  "PIN RULESETS PER TENANT — geofences and windows should be versioned, not ad hoc.",
                  "EXPORT AUDIT CHAINS DAILY for insurer and regulatory reconciliation.",
                  "TEST IN THE PLAYGROUND — /playground runs the same engines against mock telemetry.",
                ].map((b) => (
                  <li key={b} className="border-l-2 border-telemetry pl-4 py-1">{b}</li>
                ))}
              </ul>
            </Section>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Docs;
